import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { readOverviewCacheEntry, writeOverviewCache, overviewRefreshNotice } from './overviewCache';
import { applyRemovals, isStale, pruneRemovals, recordRemoval, type RemovalMap } from './attentionRemovals';

export function useOverviewData<T extends { attention_tasks: { id: string }[] }>(userId: number) {
  const [cached] = useState(() => readOverviewCacheEntry<T>(userId));
  const [data, setData] = useState<T | null>(cached?.data ?? null);
  const fetchedAt = useRef(cached?.fetchedAt ?? null);
  const [refreshFailed, setRefreshFailed] = useState(false);

  // Every task action PATCHes then refreshes this whole slow payload, so two
  // quick actions leave two fetches in flight. Without these guards the older
  // response lands last and resurrects the row Henry just removed — in the
  // list and in the cache. See lib/attentionRemovals for the seq semantics.
  const listSeq = useRef(0);
  const removed = useRef<RemovalMap>(new Map());
  // Pending coalesced refresh from `ss:mutated` (below); the page's own
  // immediate refresh cancels it so one action costs one fetch.
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    const seq = ++listSeq.current;
    api<T>('/api/admin/overview')
      .then((fresh) => {
        if (isStale(seq, listSeq.current)) return;
        const next = {
          ...fresh,
          attention_tasks: applyRemovals(fresh.attention_tasks, removed.current, seq),
        };
        pruneRemovals(removed.current, seq);
        setData(next);
        fetchedAt.current = Date.now();
        setRefreshFailed(false);
        writeOverviewCache(userId, next, fetchedAt.current);
      })
      .catch(() => {
        if (!isStale(seq, listSeq.current)) setRefreshFailed(true);
      });
  }, [userId]);

  useEffect(refresh, [refresh]);

  // Any mutation anywhere refreshes the page (build-note 4): a task added
  // from the quick-add sheet has to land in the attention list without a
  // navigation. Coalesced so one action's burst costs one fetch; ordering is
  // the seq guard's job, so a refresh racing the row's own PATCH is safe.
  useEffect(() => {
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(refresh, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      window.removeEventListener('ss:mutated', onMutated);
    };
  }, [refresh]);

  // Optimistic removal: take the row out now, and remember which refresh was
  // newest so an older in-flight response can't put it back. The cache is
  // rewritten here too — if the follow-up refresh fails (flaky network,
  // expired session) a reload must not paint the row back from it. Counts
  // are left to the server refresh.
  const dataRef = useRef(data);
  dataRef.current = data;
  const onTaskRemoved = useCallback(
    (id: string) => {
      recordRemoval(removed.current, id, listSeq.current);
      const current = dataRef.current;
      if (!current) return;
      const next = { ...current, attention_tasks: current.attention_tasks.filter((t) => t.id !== id) };
      dataRef.current = next;
      setData(next);
      writeOverviewCache(userId, next, fetchedAt.current);
    },
    [userId],
  );

  return { data, refresh, onTaskRemoved, fetchedAt: fetchedAt.current,
    refreshNotice: overviewRefreshNotice(refreshFailed, data !== null, fetchedAt.current) };
}
